export type ParamSchema =
  | { kind: 'none' }
  | { kind: 'force' }
  | { kind: 'backfill'; defaultDays: number; maxDays: number }
  | { kind: 'month' };

export interface RefreshableMetric {
  id: string;
  displayName: string;
  description: string;
  /** Builds the URL path (no leading slash) given the user-supplied params. */
  endpoint: (params: Record<string, string>) => string;
  method: 'POST';
  params: ParamSchema;
  /** Optional cron expression, rendered when known. */
  schedule?: string;
  /** Optional ISO timestamp of the last successful compute. */
  lastComputedAt?: string;
}

export interface MetricDefinitionSummary {
  id: string;
  displayName: string;
  backfillDepth: number;
  schedule?: string;
  lastComputedAt?: string;
}

export const STATIC_REFRESHABLES: RefreshableMetric[] = [
  {
    id: 'read_rate_sla',
    displayName: 'Read-rate SLA matrix',
    description:
      'Recomputes the product-type SLA matrix from the database and stores it in DynamoDB.',
    endpoint: ({ force }) => `read-rate-sla/compute?force=${force === 'true' ? 'true' : 'false'}`,
    method: 'POST',
    params: { kind: 'force' },
  },
  {
    id: 'metrics_all_enabled',
    displayName: 'All scheduled metrics (recompute all enabled)',
    description: 'Computes the most recent period for every enabled scheduled metric.',
    endpoint: () => 'metrics/compute',
    method: 'POST',
    params: { kind: 'none' },
  },
  {
    id: 'water_saved_monthly',
    displayName: 'Water-saved monthly summary',
    description: 'Recomputes the water-saved summary for the chosen month.',
    endpoint: ({ month }) => `water-saved/monthly/${month}/recompute`,
    method: 'POST',
    params: { kind: 'month' },
  },
];

export function expandMetricDefinitions(
  defs: MetricDefinitionSummary[],
): RefreshableMetric[] {
  return defs.flatMap((d) => [
    {
      id: `metric:${d.id}`,
      displayName: `${d.displayName} (latest)`,
      description: `Computes the most recent period for ${d.id}.`,
      endpoint: () => `metrics/compute/${d.id}`,
      method: 'POST' as const,
      params: { kind: 'none' as const },
      schedule: d.schedule,
      lastComputedAt: d.lastComputedAt,
    },
    {
      id: `metric:${d.id}:backfill`,
      displayName: `${d.displayName} (backfill)`,
      description: `Backfills historical periods for ${d.id}.`,
      endpoint: ({ days }) => `metrics/backfill/${d.id}?days=${days}`,
      method: 'POST' as const,
      params: {
        kind: 'backfill' as const,
        defaultDays: d.backfillDepth,
        maxDays: d.backfillDepth * 2,
      },
      schedule: d.schedule,
    },
  ]);
}

export function buildRefreshUrl(
  metric: RefreshableMetric,
  params: Record<string, string>,
): string {
  return metric.endpoint(params);
}
